/**
 * Account Settings → Password & sessions (#547 PR 5).
 *
 * One tab, two sections:
 *
 *   - Change password — Better Auth `changePassword` with
 *     `revokeOtherSessions: true` so a password change hardens every
 *     other device. Matches existing /account semantics.
 *   - Active sessions — Better Auth `listSessions` + revoke per
 *     session. The current session row carries a "Current" pill so
 *     the user can tell which row is theirs (revoking the current
 *     session is allowed but rare — sign-out is the better path).
 *
 * Two-factor auth is deliberately not here yet — enabling the Better
 * Auth `twoFactor` plugin needs schema migration, login-flow change,
 * TOTP setup UI, backup codes, recovery flows. Worth its own PR. No
 * placeholder is shown for it: a card describing work that does not
 * exist yet tells a user nothing they can act on.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Monitor, Loader2 } from 'lucide-react'
import { Button, Label, PasswordInput } from '@/components/ui'
import { authClient } from '@/auth/authClient'
import { toUserMessage } from '@/lib/userMessage'
import { useConfirm } from '@/hooks/useConfirm'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'

interface SessionRow {
  id: string
  token: string
  userAgent?: string | null
  ipAddress?: string | null
  createdAt: string | Date
  expiresAt: string | Date
}

export function AccountPasswordSessions() {
  return (
    <div className="space-y-8 max-w-xl">
      <ChangePasswordSection />
      <ActiveSessionsSection />
    </div>
  )
}

// ── Change password ────────────────────────────────────────────────────

/** Min + max password length. NIST SP 800-63B §5.1.1.2 — 8-char
 * floor for memorized secrets, 64-char minimum-acceptable maximum.
 * 800-63B explicitly forbids composition rules ("must contain
 * uppercase + symbol") and mandatory rotation. See
 * docs/adrs/0011-password-policy.md for the rationale.
 *
 * Kept in sync with Better Auth's `emailAndPassword` config
 * (`minPasswordLength` / `maxPasswordLength`) — backend is the
 * authoritative gate, this constant just informs the UI. */
const MIN_PASSWORD_LENGTH = 8
const MAX_PASSWORD_LENGTH = 64

/** Code returned by the backend when HIBP screening rejects a
 * candidate password (see `infrastructure/auth/betterAuth.ts`
 * `PASSWORD_COMPROMISED_CODE`). Used by the change-password
 * handler below to render a friendly message even if the
 * server-side text ever drifts — keying off `code` means the UI
 * stays consistent without forcing the backend message to be a
 * stable contract. */
const PASSWORD_COMPROMISED_CODE = 'PASSWORD_COMPROMISED'
const PASSWORD_COMPROMISED_FALLBACK_MESSAGE =
  'This password appears on known-breach lists. Try a different one.'

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [changedAt, setChangedAt] = useState<number | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`)
      return
    }
    if (newPassword.length > MAX_PASSWORD_LENGTH) {
      // The maxLength attribute already prevents this in happy-path
      // browsers, but a paste/programmatic-fill round-trip can
      // sometimes bypass it. The backend (Better Auth
      // `maxPasswordLength`) is the authoritative gate; this is UI
      // defense-in-depth so the user sees a clear error instead of a
      // generic server 400.
      setError(`New password must be at most ${MAX_PASSWORD_LENGTH} characters.`)
      return
    }
    setSubmitting(true)
    try {
      const { error: e } = await authClient.changePassword({
        currentPassword,
        newPassword,
        // Sign out other sessions on password change — security
        // hygiene for "I changed my password because something felt
        // off" cases.
        revokeOtherSessions: true,
      })
      if (e) {
        // Map known error codes to friendly copy. The HIBP
        // rejection has its own code so we can render a clear
        // message even if the server-side wording changes; every
        // other error falls through to the API message (which
        // Better Auth populates for the standard 4xx paths like
        // wrong current password, password too short, etc.).
        if ((e as { code?: string }).code === PASSWORD_COMPROMISED_CODE) {
          throw new Error(PASSWORD_COMPROMISED_FALLBACK_MESSAGE)
        }
        throw new Error(e.message ?? 'Failed to change password')
      }
      setCurrentPassword('')
      setNewPassword('')
      // `changedAt` bumps on success — it's also the `key` on the
      // PasswordInput fields, so a successful change remounts them
      // fresh (re-masked). Without that, a field the user revealed
      // would stay revealed for the NEXT change attempt. PR #578.
      setChangedAt(Date.now())
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't change your password. Make sure the current password is right, then try again.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold">Change password</h3>
        <p className="text-sm text-muted-foreground">
          Other devices will be signed out automatically.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="current-password">Current password</Label>
          <PasswordInput
            key={`current-${changedAt ?? 'init'}`}
            id="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            revealLabel="current password"
            required
            disabled={submitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <PasswordInput
            key={`new-${changedAt ?? 'init'}`}
            id="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            revealLabel="new password"
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            required
            disabled={submitting}
            aria-describedby="new-password-rules"
          />
          {/* Surface the rules up front so the user sets a password
              that'll pass on the first try. NIST SP 800-63B §5.1.1.2
              — length floor, NO composition rules (these don't
              improve security and frustrate users into worse choices
              per Florencio/Herley + Komanduri), NO mandatory
              rotation. Full rationale in
              docs/adrs/0011-password-policy.md. */}
          <p id="new-password-rules" className="text-xs text-muted-foreground">
            {MIN_PASSWORD_LENGTH}–{MAX_PASSWORD_LENGTH} characters. No
            composition rules. Passwords don't expire. We check against
            known-breach lists (k-anonymity, no full password ever
            leaves this server) and reject any that appear in them.
          </p>
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        {changedAt && !error && (
          <p className="text-sm text-success" key={changedAt}>
            Password changed.
          </p>
        )}
        <Button
          type="submit"
          disabled={submitting || !currentPassword || !newPassword}
        >
          {submitting ? 'Changing…' : 'Change password'}
        </Button>
      </form>
    </section>
  )
}

// ── Active sessions ────────────────────────────────────────────────────

function ActiveSessionsSection() {
  // #1215: themed confirm gate on the revoke affordances. The previous
  // one-click revoke would sign a device out instantly — recoverable
  // (you can re-sign-in) but jarring on a misclick, especially the
  // "sign out everywhere else" bulk affordance.
  const confirm = useConfirm()
  const tz = useUserTimezone()
  const [sessions, setSessions] = useState<SessionRow[] | null>(null)
  const [currentSessionToken, setCurrentSessionToken] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [bulkRevoking, setBulkRevoking] = useState(false)

  async function reload() {
    try {
      const [{ data: rows, error: e }, { data: cur }] = await Promise.all([
        authClient.listSessions(),
        // Used to badge the "Current" pill on the active session
        // row. Tolerate failure — badge just goes missing if the
        // call blips; the rest of the section still works.
        authClient.getSession().catch(() => ({ data: null })),
      ])
      if (e) throw new Error(e.message ?? 'Failed to load sessions')
      setSessions((rows ?? []) as unknown as SessionRow[])
      setCurrentSessionToken(
        (cur as { session?: { token?: string } } | null)?.session?.token ?? null,
      )
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load sessions')
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function handleRevoke(session: SessionRow) {
    const isCurrent = session.token === currentSessionToken
    const ok = await confirm({
      title: isCurrent ? 'Sign out this device?' : 'Revoke this session?',
      description: isCurrent
        ? "You're using this device right now — confirming will sign you out and bounce you to the login screen."
        : `${session.userAgent || 'That device'} will be signed out immediately. You'll need to sign in again on it.`,
      confirmLabel: isCurrent ? 'Sign out' : 'Revoke session',
      destructive: true,
    })
    if (!ok) return
    setRevoking(session.token)
    setActionError(null)
    try {
      // Better Auth surfaces failures via `{ error }`, not throw —
      // without explicit handling the UI would clear the spinner
      // and reload silently as if the revoke succeeded.
      // PR #563 Copilot review.
      const { error } = await authClient.revokeSession({ token: session.token })
      if (error) throw new Error(error.message ?? 'Failed to revoke session')
      await reload()
    } catch (err) {
      setActionError(
        toUserMessage(
          err,
          "Couldn't sign that session out. Try again — if the problem continues, refresh.",
        ),
      )
    } finally {
      setRevoking(null)
    }
  }

  async function handleRevokeAllOthers() {
    const ok = await confirm({
      title: 'Sign out every other device?',
      description:
        'Every device except this one is signed out immediately. Each will need to sign in again next time it tries to use your account.',
      confirmLabel: 'Sign out',
      destructive: true,
    })
    if (!ok) return
    setBulkRevoking(true)
    setActionError(null)
    try {
      // Better Auth's revokeOtherSessions clears every session except
      // the current one. Matches the GitHub "Log out everywhere else"
      // pattern. Same explicit-error pattern as the single-revoke
      // handler — silent failure would leave other sessions live
      // while the UI suggested otherwise. PR #563 Copilot review.
      const { error } = await authClient.revokeOtherSessions()
      if (error) throw new Error(error.message ?? 'Failed to sign out other sessions')
      await reload()
    } catch (err) {
      setActionError(
        toUserMessage(
          err,
          "Couldn't sign out the other sessions. Try again — if the problem continues, refresh.",
        ),
      )
    } finally {
      setBulkRevoking(false)
    }
  }

  const otherSessions = (sessions ?? []).filter(
    (s) => s.token !== currentSessionToken,
  )

  return (
    <section className="space-y-3 border-t pt-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">Active sessions</h3>
          <p className="text-sm text-muted-foreground">
            Devices currently signed in to your account.
          </p>
        </div>
        {otherSessions.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRevokeAllOthers}
            disabled={bulkRevoking || revoking !== null}
          >
            {bulkRevoking ? 'Signing out…' : 'Sign out everywhere else'}
          </Button>
        )}
      </div>
      {loadError && (
        <div className="space-y-2">
          <p className="text-sm text-destructive" role="alert">
            {loadError}
          </p>
          <Button variant="outline" onClick={reload}>
            Retry
          </Button>
        </div>
      )}
      {actionError && (
        <p className="text-sm text-destructive" role="alert">
          {actionError}
        </p>
      )}
      {!loadError && sessions === null && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      )}
      {!loadError && sessions !== null && sessions.length === 0 && (
        <p className="text-sm text-muted-foreground">No active sessions.</p>
      )}
      {!loadError && sessions !== null && sessions.length > 0 && (
        <ul className="divide-y divide-border border rounded-md">
          {sessions.map((s) => {
            const isCurrent = s.token === currentSessionToken
            return (
              <li
                key={s.id}
                className="flex items-center justify-between py-3 px-3 gap-3"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <Monitor
                    className="h-4 w-4 text-muted-foreground mt-0.5"
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      <span>{s.userAgent || 'Unknown device'}</span>
                      {isCurrent && (
                        <span className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary px-1.5 py-0.5 rounded">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {s.ipAddress ? `${s.ipAddress} · ` : ''}
                      Signed in {formatDate(s.createdAt, tz)}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevoke(s)}
                  disabled={revoking !== null || bulkRevoking}
                >
                  {revoking === s.token ? 'Revoking…' : 'Revoke'}
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
